import { range } from "@prismicio/editor-support/Array"
import { useDebounceState } from "@prismicio/editor-support/React"
import {
  Box,
  ErrorBoundary,
  FormSearchInput,
  MediaCard,
  ScrollArea,
} from "@prismicio/editor-ui"
import {
  type ReactNode,
  Suspense,
  useEffect,
  useState,
  useTransition,
} from "react"

import { useEditorConfig } from "../../EditorConfig"
import { NoSearchResults } from "../MediaLibrary/components/EmptyMediaList"
import { MediaLibraryRenderError } from "../MediaLibrary/components/MediaLibraryRenderError"
import { MediaCardSkeleton } from "../MediaLibrary/components/MediaLibrarySkeleton"
import type { ExternalImage } from "../MediaLibrary/hooks/mediaLibraryData"
import { type UnsplashImage, useUnsplashSearch } from "./unsplashData"

interface UnsplashLibraryProps {
  onSelectedChange?: (media: ExternalImage[]) => void
}

export function UnsplashLibrary(props: UnsplashLibraryProps) {
  const { onSelectedChange } = props

  const [isSearchingUplash, startSearchingUnsplash] = useTransition()
  const term = useDebounceState({
    initialState: "",
    ms: 300,
    startTransition: startSearchingUnsplash,
  })

  return (
    <Box flexDirection="column" gap={16} width="100%" position="relative">
      <FormSearchInput
        value={term.value}
        placeholder="Search on Unsplash.com"
        onValueChange={term.set}
      />

      <ErrorBoundary renderError={() => <MediaLibraryRenderError />}>
        <Suspense fallback={<UnsplashListSkeleton />}>
          <UnsplashList
            term={term.debouncedValue}
            loading={isSearchingUplash}
            onSelectedChange={onSelectedChange}
          />
        </Suspense>
      </ErrorBoundary>
    </Box>
  )
}

interface MediaListProps {
  term: string
  loading: boolean
  onSelectedChange?: (media: ExternalImage[]) => void
}

function UnsplashList(props: MediaListProps) {
  const { term, onSelectedChange, loading } = props

  const { media, toggleSelected, selectedImageId } = useUnsplashList({
    term,
    onSelectedChange,
  })

  return (
    <>
      {media.length > 0 && (
        <UnsplashScrollArea>
          {media?.map((media) => {
            const fullUrl = new URL(media.urls.full)
            const fileType = fullUrl.searchParams.get("fm") ?? "jpg"

            return (
              <MediaCard
                key={media.id}
                url={media.urls.small}
                filename={media.description ?? "Unsplash Image"}
                selected={selectedImageId === media.id}
                onCardClick={() => toggleSelected(media)}
                onCheckedChange={() => toggleSelected(media)}
                extension={fileType}
                kind="image"
                width={media.width}
                height={media.height}
                loading={loading}
              />
            )
          })}
        </UnsplashScrollArea>
      )}
      {media.length === 0 && <NoSearchResults />}
    </>
  )
}

interface UnsplashScrollAreaProps {
  children: ReactNode
}

function UnsplashScrollArea(props: UnsplashScrollAreaProps) {
  const { children } = props

  return (
    <ScrollArea
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 250px), 1fr))",
        gap: 16,
        padding: 2,
        paddingBottom: 16,
        position: "relative",
        width: "100%",
      }}
    >
      {children}
    </ScrollArea>
  )
}

function UnsplashListSkeleton() {
  return (
    <UnsplashScrollArea>
      {range(1, 24).map((num) => (
        <MediaCardSkeleton key={num} />
      ))}
    </UnsplashScrollArea>
  )
}

interface UseUnsplashListParams {
  term: string
  onSelectedChange?: (media: ExternalImage[]) => void
}

function useUnsplashList(params: UseUnsplashListParams) {
  const { term, onSelectedChange } = params
  const { unsplashApiBaseUrl } = useEditorConfig()
  const media = useUnsplashSearch(unsplashApiBaseUrl, term || "citrus fruit")
  const firstImageId = media?.[0]?.id

  const [selectedImageId, setSelectedImageId] = useState<string>()

  useEffect(() => {
    onSelectedChange?.([])
    setSelectedImageId(undefined)
  }, [firstImageId, onSelectedChange])

  function toggleSelected(media: UnsplashImage) {
    if (media.id === selectedImageId) {
      setSelectedImageId(undefined)
      return onSelectedChange?.([])
    }

    setSelectedImageId(media.id)
    onSelectedChange?.([
      {
        ...media,
        url: media.urls.full,
        alt: media.alt_description,
      },
    ])
  }

  return { media, toggleSelected, selectedImageId }
}
